import PropTypes from 'prop-types';
import { Card, Stack, Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';
import { TEAM_FONT } from 'src/sections/@dashboard/user/teamTokens';
import { cardSx } from './RecordPieces';

// ----------------------------------------------------------------------
// A block of the record: one hairline card with a plain heading.
//
// It used to wear a 44px tinted tile in a different colour per section and a
// sentence of explanation under every title. Down a page of nine blocks that
// read as a toy, and the nine grey sentences buried the headings they were
// meant to introduce. The icon is now a small monochrome mark, and anything
// still worth saying sits on the heading line as a quiet note.

SectionCard.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  color: PropTypes.string,
  children: PropTypes.node,
  disableContentPadding: PropTypes.bool,
  sx: PropTypes.object,
  contentSx: PropTypes.object
};

export default function SectionCard({
  icon,
  title,
  subtitle,
  action,
  children,
  disableContentPadding = false,
  sx,
  contentSx,
  ...other
}) {
  const theme = useTheme();
  return (
    <Card sx={{ ...cardSx(theme), ...sx }} {...other}>
      {(title || action) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.75,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ minWidth: 0 }}
          >
            {icon && (
              <Iconify
                icon={icon}
                width={18}
                height={18}
                sx={{ color: 'text.disabled', flexShrink: 0 }}
              />
            )}
            <Typography
              sx={{
                fontFamily: TEAM_FONT,
                fontWeight: 700,
                fontSize: 15.5,
                lineHeight: 1.3,
                minWidth: 0
              }}
              noWrap
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                noWrap
                sx={{
                  color: 'text.disabled',
                  display: { xs: 'none', md: 'block' }
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Stack>

          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Stack>
      )}

      <Box
        sx={{ p: disableContentPadding ? 0 : { xs: 2, sm: 2.5 }, ...contentSx }}
      >
        {children}
      </Box>
    </Card>
  );
}
